import { t } from "../../i18n";
import { useCallback, useEffect, useState } from "react";
import { Coins, Cpu, Hash, LoaderCircle } from "lucide-react";
import { api } from "../../api";
import { Metric } from "../shared/UsageMetric";

type UsageAggregate = {
  requests: number;
  promptTokens: number;
  completionTokens: number;
  cacheReadTokens: number;
  cacheWriteTokens: number;
  totalTokens: number;
  costMicros: number;
};

type ConversationUsage = {
  currency: string;
  totals: UsageAggregate;
  byModel: Array<
    UsageAggregate & {
      modelName: string;
      modelRemoteId: string;
      kind: string;
    }
  >;
};

const number = new Intl.NumberFormat(undefined, {
  notation: "compact",
  maximumFractionDigits: 1,
});

function money(micros: number, currency: string) {
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 6,
  }).format(micros / 1_000_000);
}

export function ChatUsagePanel({
  conversationId,
  usageRevision,
  onClose: _onClose,
}: {
  conversationId: string;
  usageRevision: number;
  onClose?: () => void;
}) {
  const [data, setData] = useState<ConversationUsage>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    setError("");
    void api<ConversationUsage>(`/conversations/${conversationId}/usage`)
      .then(setData)
      .catch((reason) =>
        setError(
          reason instanceof Error
            ? reason.message
            : t("Usage could not be loaded"),
        ),
      )
      .finally(() => setLoading(false));
  }, [conversationId]);

  useEffect(() => {
    load();
  }, [load, usageRevision]);

  const totals = data?.totals;
  const promptRatio = totals?.totalTokens
    ? (totals.promptTokens / totals.totalTokens) * 100
    : 0;

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
      <div className="flex h-14 shrink-0 items-center gap-2 border-b border-stone-200 px-4 dark:border-white/10">
        <div className="grid size-7 place-items-center rounded-lg bg-lumen-100 text-lumen-700 dark:bg-lumen-900/40 dark:text-lumen-300">
          <Cpu size={15} />
        </div>
        <div className="min-w-0">
          <h2 className="truncate text-sm font-bold">{t("Chat usage")}</h2>
          <p className="text-[11px] text-stone-500">
            {t("Tokens and estimated cost")}
          </p>
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto p-4">
        {loading && !data ? (
          <div className="grid min-h-48 place-items-center text-stone-400">
            <LoaderCircle className="animate-spin" />
          </div>
        ) : error ? (
          <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300">
            {error}
          </div>
        ) : data && totals ? (
          <div className="space-y-5">
            <div className="grid grid-cols-2 gap-2">
              <Metric
                label={t("Requests")}
                value={number.format(totals.requests)}
                icon={Hash}
              />
              <Metric
                label={t("Total tokens")}
                value={number.format(totals.totalTokens)}
                icon={Cpu}
              />
              <Metric
                label={t("Prompt / output")}
                value={`${number.format(totals.promptTokens)} / ${number.format(totals.completionTokens)}`}
                icon={Cpu}
              />
              <Metric
                label={t("Cached input")}
                value={number.format(totals.cacheReadTokens)}
                icon={Cpu}
              />
              <Metric
                label={t("Estimated cost")}
                value={money(totals.costMicros, data.currency)}
                icon={Coins}
              />
            </div>

            <section>
              <h3 className="text-xs font-bold tracking-wider text-stone-500 uppercase">
                {t("Token split")}
              </h3>
              <div className="mt-3 h-2 overflow-hidden rounded-full bg-stone-100 dark:bg-white/10">
                <div
                  className="h-full rounded-full bg-lumen-500"
                  style={{ width: `${promptRatio}%` }}
                />
              </div>
              <div className="mt-2 flex justify-between text-xs text-stone-500">
                <span>
                  {t("Prompt · {{count}}", {
                    count: number.format(totals.promptTokens),
                  })}
                </span>
                <span>
                  {t("Output · {{count}}", {
                    count: number.format(totals.completionTokens),
                  })}
                </span>
              </div>
            </section>

            <section>
              <h3 className="text-xs font-bold tracking-wider text-stone-500 uppercase">
                {t("By model")}
              </h3>
              {data.byModel.length ? (
                <div className="mt-2 divide-y divide-stone-200 rounded-xl border border-stone-200 dark:divide-white/10 dark:border-white/10">
                  {data.byModel.map((row) => (
                    <div
                      key={`${row.modelName}-${row.modelRemoteId}-${row.kind}`}
                      className="p-3"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="truncate text-sm font-semibold">
                            {row.modelName?.trim() ||
                              row.modelRemoteId ||
                              t("Assistant")}
                          </div>
                          {row.kind === "command-approval" && (
                            <div className="text-[11px] font-medium text-lumen-700 dark:text-lumen-300">
                              {t("Command approval model")}
                            </div>
                          )}
                          <div className="truncate text-[11px] text-stone-500">
                            {row.modelRemoteId}
                          </div>
                        </div>
                        <div className="shrink-0 text-right text-sm font-semibold">
                          {money(row.costMicros, data.currency)}
                        </div>
                      </div>
                      <div className="mt-2 flex justify-between text-xs text-stone-500">
                        <span>{t("requests", { count: row.requests })}</span>
                        <span>
                          {t("tokens", {
                            count: row.totalTokens,
                          })}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="mt-2 rounded-xl border border-dashed border-stone-200 p-4 text-sm text-stone-500 dark:border-white/10">
                  {t("No usage recorded for this chat yet.")}
                </p>
              )}
            </section>

            <p className="text-xs leading-5 text-stone-500">
              {t(
                "Costs are estimates based on model pricing captured when each request was made.",
              )}
            </p>
          </div>
        ) : null}
      </div>
    </div>
  );
}
